// =====================================================================
// Définition des 3 types de course (FIS / Swiss-Ski / Populaire) et des
// secteurs (AL / TM / Autre). Tout le formulaire Raceheader, la
// validation et les panneaux d'export sont pilotés par ce fichier :
// pour changer une règle (champ obligatoire, masqué...), on modifie
// uniquement FIELD_RULES ci-dessous.
// =====================================================================

export const RACE_TYPES = [
    { value: "fis", label: "FIS", description: "Codex FIS, points FIS, envoi XML à la FIS" },
    { value: "swissski", label: "Swiss-Ski", description: "Codex Swiss-Ski, points Swiss-Ski, XML Integral" },
    { value: "populaire", label: "Populaire", description: "Course libre, classements par catégorie, sans points" },
];

export const SECTORS = [
    { value: "AL", label: "Alpin (AL)" },
    { value: "TM", label: "Télémark (TM)" },
    { value: "OTHER", label: "Autre" },
];

// swissOnly : discipline propre à Swiss-Ski, non proposée pour une course FIS.
// ⚠️ Codes Télémark à confirmer avec la doc XML FIS Télémark (absente du
// projet) : CL = Classic, SP = Sprint, PSP = Parallel Sprint.
export const DISCIPLINES = {
    AL: [
        { value: "SL", label: "SL — Slalom" },
        { value: "GS", label: "GS — Riesenslalom" },
        { value: "SG", label: "SG — Super-G" },
        { value: "DH", label: "DH — Downhill / Abfahrt" },
        { value: "AC", label: "AC — Alpine-Kombi" },
        { value: "PSL", label: "PSL — Parallel Slalom (FIS)" },
        { value: "PGS", label: "PGS — Parallel Giant Slalom (FIS)" },
        { value: "CR", label: "CR — Combi-Race (nur SUI)", swissOnly: true },
        { value: "PS", label: "PS — Parallel-Slalom (nur SUI)", swissOnly: true },
    ],
    TM: [
        { value: "GS", label: "GS — Giant Slalom" },
        { value: "CL", label: "CL — Classic" },
        { value: "SP", label: "SP — Sprint" },
        { value: "PSP", label: "PSP — Parallel Sprint" },
    ],
    OTHER: [], // saisie libre
};

export function disciplineOptions(raceType, sector) {
    const list = DISCIPLINES[sector] || [];
    return raceType === "fis" ? list.filter((d) => !d.swissOnly) : list;
}

export function disciplineLabel(config) {
    const found = (DISCIPLINES[config.sector] || []).find((d) => d.value === config.discipline);
    return found?.label || config.discipline || "";
}

export const RTYP_OPTIONS = [
    { value: "JOANI", label: "JOANI — JO Animation" },
    { value: "JORVA", label: "JORVA — JO Punkterennen A" },
    { value: "JORVB", label: "JORVB — JO Punkterennen B" },
    { value: "JOIR", label: "JOIR — JO Interregion" },
    { value: "JONAT", label: "JONAT — JO National" },
    { value: "REGA", label: "REGA — Regionales A-Rennen" },
    { value: "REGB", label: "REGB — Regionales B-Rennen" },
    { value: "REGC", label: "REGC — Regionales C-Rennen" },
    { value: "MAS", label: "MAS — Masters" },
    { value: "VOLK", label: "VOLK — Volksrennen" },
    { value: "COC", label: "COC — Continental Cup" },
    { value: "FIS", label: "FIS" },
    { value: "WC", label: "WC — Weltcup" },
];

// Catégories FIS courantes (liste indicative, champ reste éditable).
export const FIS_CATEGORY_OPTIONS = ["WC", "EC", "NAC", "SAC", "ANC", "FEC", "FIS", "NJR", "NJC", "ENL", "CIT", "UNI", "YOG", "WJC"];

export const GENDER_OPTIONS = [
    { value: "M", label: "M — Hommes" },
    { value: "W", label: "W — Femmes" },
    { value: "A", label: "A — Mixte" },
];

// required | recommended | optional | hidden
// "category" pointe vers une clé de config différente selon le type
// (voir CATEGORY_KEY) : RTYP Swiss-Ski ≠ Category FIS.
export const FIELD_RULES = {
    fis: {
        codex: "required", date: "required", event: "recommended", place: "recommended",
        season: "required", nation: "required", category: "required", discipline: "required",
        gender: "required", sector: "required",
    },
    swissski: {
        codex: "required", date: "required", event: "recommended", place: "recommended",
        season: "optional", nation: "optional", category: "required", discipline: "required",
        gender: "optional", sector: "required",
    },
    populaire: {
        codex: "hidden", date: "required", event: "optional", place: "optional",
        season: "hidden", nation: "optional", category: "optional", discipline: "optional",
        gender: "optional", sector: "optional",
    },
};

export const CATEGORY_KEY = { fis: "fisCategory", swissski: "rtyp", populaire: "category" };

// Pénalités / points : seulement pour FIS et Swiss-Ski, et uniquement en
// Alpin tant que le backend (fisPointsRouter.js / swissski-calc) ne gère
// pas le Télémark.
export function penaltySupport(config) {
    if (config.raceType === "populaire") return { supported: false, reason: "Pas de points pour une course populaire." };
    // Télémark (FIS) : GS, CL et SP — F = 500, max 200 (art. 1715).
    if (config.sector === "TM") {
        if (config.raceType !== "fis") return { supported: false, reason: "Points télémark : uniquement pour les courses FIS." };
        if (!["GS", "CL", "SP"].includes(String(config.discipline || "").toUpperCase()))
            return { supported: false, reason: "Points télémark calculés pour GS, CL et SP uniquement (pas pour les parallèles)." };
        return { supported: true, telemark: true };
    }
    if (config.sector !== "AL")
        return { supported: false, reason: "Le calcul des points n'est implémenté que pour l'Alpin (AL) et le Télémark (TM)." };
    return { supported: true };
}

// Swiss-Ski : format du codex à confirmer — on exige seulement un
// identifiant sans espace. Ajuster cette regex quand le format est connu.
export const SWISSSKI_CODEX_PATTERN = /^[0-9A-Za-z-]+$/;

export function deriveSeason(dateStr) {
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec((dateStr || "").trim());
    if (!m) return "";
    const month = Number(m[2]);
    const year = Number(m[3]);
    // Saison FIS : juillet -> juin, identifiée par l'année de fin.
    return String(month >= 7 ? year + 1 : year);
}

function isValidDate(dateStr) {
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec((dateStr || "").trim());
    if (!m) return false;
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return d.getFullYear() === Number(m[3]) && d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[1]);
}

export function fieldValue(config, field) {
    if (field === "category") return config[CATEGORY_KEY[config.raceType]] || "";
    return config[field] || "";
}

// Retourne { errors, warnings } : errors bloque les exports officiels,
// warnings est purement informatif (champs recommandés vides).
export function validateHeader(config) {
    const rules = FIELD_RULES[config.raceType] || {};
    const errors = {};
    const warnings = {};

    Object.entries(rules).forEach(([field, rule]) => {
        const value = String(fieldValue(config, field)).trim();
        if (rule === "required" && !value) errors[field] = "Obligatoire";
        if (rule === "recommended" && !value) warnings[field] = "Recommandé";
    });

    if (config.date && !isValidDate(config.date)) errors.date = "Format JJ.MM.AAAA attendu";

    if (config.codex && rules.codex !== "hidden") {
        if (config.raceType === "fis" && !/^\d{1,4}$/.test(config.codex.trim()))
            errors.codex = "4 chiffres maximum, sans préfixe nation";
        if (config.raceType === "swissski" && !SWISSSKI_CODEX_PATTERN.test(config.codex.trim()))
            errors.codex = "Sans espace ni caractère spécial";
    }
    if (config.nation && !/^[A-Z]{3}$/.test(config.nation)) errors.nation = "Code FIS à 3 lettres";
    if (config.season && !/^\d{4}$/.test(String(config.season))) errors.season = "Année sur 4 chiffres";

    return { errors, warnings, isValid: Object.keys(errors).length === 0 };
}

// Valeurs par défaut à la création d'une course, selon le type choisi.
export function defaultsForType(raceType) {
    return {
        raceType,
        sector: "AL",
        nation: raceType === "populaire" ? "" : "SUI",
    };
}

// Compatibilité : les configs existantes (ancienne page Swiss-Ski) n'ont
// pas de raceType. On le déduit sans rien écraser.
export function inferRaceType(cfg) {
    if (cfg.raceType) return cfg.raceType;
    if (cfg.codex || cfg.rtyp) return ["FIS", "COC", "WC"].includes(cfg.rtyp) ? "fis" : "swissski";
    return null; // nouvelle course -> écran de choix du type
}